/**
 * Staff authenticate on their own guard, separate from patients (CLAUDE.md
 * §3.3). Roles are data and must never be branched on — permissions are
 * (CLAUDE.md §3.3).
 */
export interface StaffUser {
  readonly id: string;
  readonly name: string;
  /** «عنوان» — see StaffMember. Null until somebody sets one. */
  readonly title: string | null;
  readonly email: string;
  readonly roles: readonly string[];
  readonly permissions: readonly string[];
}

export interface StaffSession {
  readonly token: string;
  readonly expiresAt: Date;
  readonly user: StaffUser;
}

/** The permission every route of the scheduling panel is gated on. */
export const SCHEDULE_MANAGE = 'schedule.manage';

/**
 * «افزایش محدود ظرفیت» — opening a few extra seats on a day, up to the policy's
 * «سقف نوبت اضافه در روز», without being able to change the scheduled capacity.
 * Shipped to the admin; the clinic hands it to the منشی in نقش‌ها. Its holder
 * reaches the وقت‌دهی section, on the day preview only.
 */
export const CAPACITY_EXTEND = 'capacity.extend';

/** `capacity.manage` — raising or lowering any interval, without a bound. */
export const CAPACITY_MANAGE = 'capacity.manage';

/** The permission the access-control section is gated on (CLAUDE.md §3.3). */
export const ROLES_MANAGE = 'roles.manage';

/**
 * Blocking a patient account — the کاربران section (CLAUDE.md §3.3). Admin-only
 * as shipped, but checked as a permission and never as a role, because roles
 * are data and a custom one may hold it tomorrow.
 */
export const ACCOUNTS_MANAGE = 'accounts.manage';

/**
 * Reading the داشبورد — the appointments table and the capacity report. The one
 * panel permission a **secretary** holds: read-only over appointments is
 * exactly their role (CLAUDE.md §3.3).
 */
export const APPOINTMENTS_VIEW = 'appointments.view';

/**
 * Booking a نوبت on a patient's behalf — «رزرو توسط منشی». Held by the admin
 * and the doctor and deliberately **not** by the secretary, whose role is
 * read-only over appointments (CLAUDE.md §3.3).
 */
export const APPOINTMENTS_CREATE = 'appointments.create';

/**
 * «ثبت حضور» — scanning a رسید at the desk and recording that the patient
 * turned up.
 *
 * Its own permission, held by the **secretary** as shipped: the front desk
 * admits patients, and doing so is neither booking nor cancelling. Like every
 * other permission here it is data — an admin can hand it to the doctor's role
 * or to a custom «پذیرش» role in the نقش‌ها screen without a deploy, which is
 * exactly why this is a permission check and never a role check (§3.3).
 */
export const APPOINTMENTS_MARK_ATTENDANCE = 'appointments.mark_attendance';

/**
 * «صف بیماران» — reading today's arrivals, with each patient's **whole** کد ملی.
 *
 * The only permission in the panel that unmasks an identifier, and the only
 * screen that shows one: the دستیار pastes the number into the نسخهٔ الکترونیک,
 * so a masked one would make the list useless. It is scoped to today, audited
 * on every read, and held as shipped by the admin and the doctor — not by the
 * secretary, who admits these very patients and still sees masked identifiers
 * everywhere they look (CLAUDE.md §3.3, §3.8).
 *
 * There is no «دستیار» role in the system. Roles are data: the clinic builds
 * one in the نقش‌ها screen and grants it this, which is exactly why this is a
 * permission check and never a role check.
 */
export const APPOINTMENTS_VIEW_ARRIVALS = 'appointments.view_arrivals';

/**
 * «نسخه نوشته شد» — recording that the دستیار has dealt with a patient, which
 * takes them out of the queue.
 *
 * Its own permission and deliberately **not** clinical: it says a patient was
 * seen, never a word about what for, so it can be granted to an assistant who
 * must not reach a single visit note. Separate from `view_arrivals` above so a
 * role can hold the tick without the identifiers.
 */
export const APPOINTMENTS_MARK_PRESCRIBED = 'appointments.mark_prescribed';

/**
 * «ویرایش نوبت» — moving a نوبت to another time or another patient, correcting
 * its حضور after the fact, or correcting the مبلغ it was booked at.
 *
 * Held by the admin **and the doctor**, matching the two who already book,
 * cancel and manage the schedule: the doctor rearranging their own day without
 * asking an admin is the point of it. The secretary holds none of it, so
 * «read-only over appointments» stays true of them (CLAUDE.md §3.3).
 *
 * It never moves money — correcting a price changes what the نوبت *says* it
 * costs, not what was paid.
 */
export const APPOINTMENTS_EDIT = 'appointments.edit';

/**
 * «حذف نوبت» and its «سطل بازیافت» — removing a booking that should never have
 * existed, and putting it back.
 *
 * Admin only, and deliberately apart from `appointments.edit`: editing is the
 * desk's daily correction work, while deleting hides a booking from every
 * report the clinic runs. Restoring costs the same permission — a role that can
 * put something in the bin must be able to take it out, or it can only make the
 * mistake (§3.3).
 *
 * Emphatically not «لغو نوبت», which is `appointments.cancel`: that one refunds
 * money and texts the patient.
 */
export const APPOINTMENTS_DELETE = 'appointments.delete';

/**
 * «حذف اکانت» — taking a patient account off every screen and **releasing its
 * mobile number**.
 *
 * Admin only, and its own permission rather than a corner of `accounts.manage`,
 * because it is a different amount of damage: managing an account blocks or
 * edits it, this takes a person's whole history off the panel and frees their
 * number for whoever registers it next.
 *
 * It is **not** the strong form of blocking. A block keeps the number *taken*,
 * which is the mechanism by which it refuses somebody; this gives it back
 * (§3.3).
 */
export const ACCOUNTS_DELETE = 'accounts.delete';

/**
 * Reading the clinic's money — «پرداخت‌ها» and a patient's اعتبار.
 *
 * Held by the admin and the **secretary**: answering «پول این نوبت کجاست؟» at
 * the desk is their job. Deliberately not the doctor's — money is not medicine,
 * which is the line CLAUDE.md §3.3 draws and this constant carries.
 */
export const PAYMENTS_VIEW = 'payments.view';

/**
 * Reading a patient's پیگیری — the messages and files they sent after a visit.
 *
 * Held by the doctor and the admin, and deliberately **not** by the secretary:
 * the desk may *file* a document into a record (`attachments.upload`) and may
 * not read what is in one. That asymmetry is §3.3's, not this file's.
 */
export const VISITS_READ_CLINICAL = 'visits.read_clinical';

/**
 * Writing a reply to a patient. The doctor's alone — not even the admin holds
 * it, because answering a patient about their own visit is clinical advice
 * (CLAUDE.md §3.3).
 */
export const VISITS_WRITE_CLINICAL = 'visits.write_clinical';

/**
 * «مجوز ارسال آزمایش» — letting a patient send their lab results once for a
 * paid review. The doctor's and the admin's as shipped; roles are data.
 */
export const LAB_REVIEWS_GRANT = 'lab_reviews.grant';

/**
 * «بررسی آزمایش» — the panel section listing the lab results patients sent and
 * their تِرِدها, apart from «پیام‌ها». Answering is still `visits.write_clinical`.
 */
export const LAB_REVIEWS_REVIEW = 'lab_reviews.review';

/**
 * «مشاوره‌های امروز» — the day's phone consultations with each patient's
 * number, «تماس», and how each call ended. The doctor's and the admin's.
 */
export const CONSULTATIONS_CONDUCT = 'consultations.conduct';

/**
 * Attaching a document to a record without being able to read one. The desk's
 * permission: a paper lab result handed over at the counter has to end up in
 * the patient's record.
 */
export const ATTACHMENTS_UPLOAD = 'attachments.upload';

/**
 * «ارسال پیام» — writing to patients in the clinic's name: one of them, all of
 * them, or everyone booked on a day. Admin-only as shipped (§3.3).
 */
export const SMS_BROADCAST = 'sms.broadcast';

/**
 * «مدیریت سایت» — editing the public landing page: its copy, images, notice
 * and SEO. Admin-only as shipped; the server checks it on every call.
 */
export const LANDING_MANAGE = 'landing.manage';

/**
 * The panel is worth opening if any one of its sections is. Gating the shell on
 * `schedule.manage` alone would lock out a custom role built to do nothing but
 * manage access — and roles are data, so that role can exist tomorrow.
 *
 * Every section's permission belongs here. «صف بیماران», «مشاوره‌ها» and
 * «بررسی آزمایش» were missing, so a role built for the queue alone was turned
 * away at the door unless it also held `appointments.view` (2026-10-27).
 */
export const PANEL_PERMISSIONS: readonly string[] = [
  APPOINTMENTS_VIEW,
  APPOINTMENTS_VIEW_ARRIVALS,
  APPOINTMENTS_MARK_ATTENDANCE,
  CONSULTATIONS_CONDUCT,
  LAB_REVIEWS_REVIEW,
  SCHEDULE_MANAGE,
  CAPACITY_EXTEND,
  ROLES_MANAGE,
  ACCOUNTS_MANAGE,
  VISITS_READ_CLINICAL,
  PAYMENTS_VIEW,
  SMS_BROADCAST,
  LANDING_MANAGE,
];
